import { useCallback, useEffect, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { useParams } from "react-router-dom";
import Reorderable from '../../Reorderable'
import Rule, { Option } from '../../Rule'
import moment from "moment"

import AddIcon from '@mui/icons-material/Add';

import { getRecordIdentity, getRemoteId } from '../../../lib/DataModel'
import type { ClientRecord, DegreeProgramRecord, ExclusionRuleRecord } from '../../../lib/DataModel'

import { InitializedRecord } from '@orbit/records'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import Box from '@mui/material/Box';
import { getIn, Prompt } from 'components/Formik/forms'
import { useNotifications } from 'lib/NotificationsProvider';
import { PageSection } from 'components/UI/Structure/PageSection';
import SectionTitle from 'components/UI/SectionTitle';
import { useOrbit } from 'providers/OrbitProvider';
import { useCurrentUser } from 'lib/CurrentUserProvider';

type ClientOrDegreeProgramRecord = ClientRecord | DegreeProgramRecord

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    marginTop: theme.spacing(2),
  },
}));

function ExclusionRule({ exclusionRule: rule, onChange, fields, permitEdit }) {
  const { store } = useOrbit()

  const removeExclusionRule = useCallback(() => {
    const appliesTo = store.cache.query(q => q.findRelatedRecord(rule, 'appliesTo')) as ClientOrDegreeProgramRecord
    const otherRules = store.cache.query(q => q.findRelatedRecords(appliesTo, 'exclusionRules')) as ExclusionRuleRecord[]

    otherRules
      .filter(r => (r.attributes.position ?? 0) > rule.attributes.position)
      .forEach(r => { r.attributes.position = (r.attributes.position ?? 0) - 1 })
    store.update(q => q.removeRecord(rule)).then(() => onChange());
  }, [store, onChange, rule])

  const fieldToName = useMemo(() => (
    Object.fromEntries(fields.map(field => [getRemoteId(field), field.attributes.name]))
  ), [fields])

  const field = store.cache.query(q => q.findRelatedRecord(rule, 'field')) as InitializedRecord

  return (
    <Box my={6}>
      <Rule
        entityName="Field"
        entityId={getRemoteId(field) ?? ''}
        setEntityId={fieldId => { rule.relationships.field = { data: getRecordIdentity('field', fieldId) }; onChange() }}
        entities={fieldToName}
        entityOptions={field?.attributes?.options as Option[] || undefined}
        operator={rule.attributes.operator}
        setOperator={val => { rule.attributes.operator = val; onChange() }}
        value={rule.attributes.value}
        setValue={val => { rule.attributes.value = val; onChange() }}
        reorderable
        onRemove={removeExclusionRule}
        permitEdit={permitEdit}
        dayOfWeek={rule.attributes.dayOfWeek}
        setDayOfWeek={val => {rule.attributes.dayOfWeek = val; onChange()}}
        startTimeOfDay={moment.tz(rule.attributes.startTimeOfDay, 'UTC').format('HH:mm')}
        setStartTimeOfDay={val => {
          rule.attributes.startTimeOfDay = (val != null ? moment(`2000-01-01T${val}:00.000Z`) : null);
          onChange()
        }}
        endTimeOfDay={moment.tz(rule.attributes.endTimeOfDay, 'UTC').subtract(1, 'minute').format('HH:mm')}
        setEndTimeOfDay={val => {
          rule.attributes.endTimeOfDay = (val != null ? moment(`2000-01-01T${val}:00.000Z`).add(1, 'minute') : null);
          onChange()
        }}
      />
    </Box>
  )
}

function fetchFields(remote, clientId) {
  return remote.query(q => q.findRelatedRecords({type: 'client', id: clientId}, 'fields'))
}

export default function ExclusionRulesEdit({ clientId: clientIdFromProps = null }) {
  const classes = useStyles();
  const { remote, store } = useOrbit()
  const { addNotification } = useNotifications()
  const { clientId, degreeProgramId } = useParams()
  const [appliesTo, setAppliesTo] = useState<DegreeProgramRecord | ClientRecord>({ type: 'client', id: '', attributes: {} }); // Initialize with a default value to avoid having to handle `undefined` case
  const [rules, setRules] = useState<ExclusionRuleRecord[]>([]);
  const [fields, setFields] = useState();

  const [loading, setLoading] = useState(true);
  const [isSaving, setSaving] = useState(false);
  const [isDirty, setDirty] = useState(false);

  const appliesToType = degreeProgramId ? 'degreeProgram' : 'client'
  const appliesToId = degreeProgramId ?? clientId ?? clientIdFromProps ?? ''

  const { getClientRole } = useCurrentUser()
  const hasPermitEditRole = !!getIn(getClientRole(clientId), 'attributes.permitEdit')
  const permitEdit = hasPermitEditRole

  useEffect(() => {
    setLoading(true)
    const promises = [
      remote.query(q => q.findRecord({type: appliesToType, id: appliesToId})),
      remote.query(q => q.findRelatedRecords({type: appliesToType, id: appliesToId}, 'exclusionRules')),
      fetchFields(remote, clientId || clientIdFromProps),
    ];
    Promise.all(promises).then(([appliesTo, rules, fields]) => {
      setAppliesTo(appliesTo);
      setRules(rules);
      setFields(fields)
      setLoading(false);
    }).catch(error => {
      console.log(error);
      addNotification({variant: 'alert', message: 'Error loading exclusion rules'})
      setLoading(false);
    })
  }, [remote, addNotification, appliesToId, appliesToType, clientId, clientIdFromProps]);

  const addExclusionRule = () => {
    const maxPosition = Math.max(0, ...store.cache.query<ExclusionRuleRecord[]>(q => q.findRelatedRecords(appliesTo, 'exclusionRules'))?.map(r => r.attributes.position ?? 0))
    store
      .update(q => q.addRecord({type: 'exclusionRule', attributes: {value: [], position: maxPosition+1}, relationships: { appliesTo: {data: {type: appliesToType, id: appliesTo.id}} }}))
      .then(() => { setDirty(true); setRules([...rules]); })
  };

  const moveRule = ({object: dragRule}, hoverRule) => {
    const allRules = store.cache.query<ExclusionRuleRecord[]>(q => q.findRelatedRecords(appliesTo, 'exclusionRules'))

    const dragRulePosition  = dragRule.attributes.position;
    const hoverRulePosition = hoverRule.attributes.position;

    allRules
      .filter(r => (r.attributes.position ?? 0) > dragRulePosition)
      .forEach(r => { r.attributes.position = (r.attributes.position ?? 0) - 1 });

    allRules
      .filter(r => (r.attributes.position ?? 0) >= hoverRulePosition)
      .forEach(r => { r.attributes.position = (r.attributes.position ?? 0) + 1 });

    dragRule.attributes.position = hoverRulePosition;

    setDirty(true);
    setRules([...rules])
  }

  if(loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
        <CircularProgress/>
      </div>
    )
  } else {
    const save = () => {
      setSaving(true);
      const rules = store.cache.query<ExclusionRuleRecord[]>(q => q.findRelatedRecords(appliesTo, 'exclusionRules'))
      const serializedRules = rules.map((rule) => {
        const serializedRule = remote.requestProcessor.serializer.serializeRecord(rule)
        return {
          id: rule.id,
          ...serializedRule.attributes,
          'field-id': getIn(serializedRule, 'relationships.field.data.id'),
        }
      })

      remote.requestProcessor.fetch(remote.requestProcessor.urlBuilder.resourceURL(appliesToType, appliesTo.id), {
        method: 'PATCH',
        json: {
          exclusion_rules: serializedRules,
        }
      })
      .then(() => {
        addNotification({variant: 'notice', message: "Your changes have been saved."})
        setDirty(false);
      })
      .catch((e) => {
        console.log(e)
        addNotification({variant: 'alert', message: "Error saving changes"});
      })
      .finally(() => setSaving(false))
    }

    return (
      <>
        <Prompt
          when={isDirty}
          message='You have unsaved changes. Are you sure you want to leave without saving?'
        />

        <PageSection>
          <SectionTitle title={`${degreeProgramId ? 'Program' : 'Client'} Exclusion Rules`} />
          <Typography variant="h6">
            Exclude users who meet any of the following criteria:
          </Typography>
          <List>
            <DndProvider backend={HTML5Backend}>
              {store.cache.query<ExclusionRuleRecord[]>(q => q.findRelatedRecords(appliesTo, 'exclusionRules').sort('position')).map(exclusionRule => (
                <Reorderable key={exclusionRule.id} object={exclusionRule} type="ExclusionRule" updatePosition={moveRule}>
                  <ExclusionRule exclusionRule={exclusionRule} fields={fields} onChange={() => { setDirty(true); setRules([...rules]); }} permitEdit={permitEdit} />
                </Reorderable>
              ))}
            </DndProvider>
          </List>
          {permitEdit &&
            <Button onClick={addExclusionRule}>
              <AddIcon/>
              Add rule
            </Button>
          }
        </PageSection>

        <div className={classes.buttons}>
          <Button disabled={isSaving || !isDirty || !permitEdit} onClick={() => save()}>
            { isSaving ? 'Please wait...' : 'Save' }
          </Button>
          {isSaving && <CircularProgress size={32}/>}
        </div>
      </>
    );
  }
}
